import { useState, useRef, useEffect, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';

const Header = forwardRef(({ isLoggedIn, onLogout, onHeightChange }, ref) => {
  const { hasRole, loading } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);
  const aboutDropdownRef = useRef(null);
  const headerRef = useRef(null);

  const isAdmin = !loading && hasRole('admin');

  // Add scroll event listener to detect when user scrolls
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Clean up the event listener
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Measure header height and pass it to parent component
  useEffect(() => {
    if (headerRef.current && onHeightChange) {
      const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          onHeightChange(entry.target.offsetHeight);
        }
      });

      resizeObserver.observe(headerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [onHeightChange]);

  // Expose the headerRef to the parent component via forwardRef
  useEffect(() => {
    if (ref) {
      ref.current = headerRef.current;
    }
  }, [ref]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Handle mouse enter and leave for About dropdown
  const handleAboutMouseEnter = () => {
    setIsAboutDropdownOpen(true);
  };

  const handleAboutMouseLeave = () => {
    setIsAboutDropdownOpen(false);
  };

  // For mobile - keep the toggle functionality
  const toggleAboutDropdown = () => {
    setIsAboutDropdownOpen(!isAboutDropdownOpen);
  };



  return (
    <header
      ref={headerRef}
      className={`bg-white shadow-md ${isScrolled ? 'fixed top-0 left-0 right-0 z-50 animate-slideDown' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img
                  src={`${process.env.PUBLIC_URL}/apn-logos/APN-blue (1).png`}
                  alt="APN Logo"
                  className="h-8"
                />
              </Link>
            </div>
            <nav className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-8">
              {/* Navigation Links */}
              <div
                className="relative flex items-center h-16"
                ref={aboutDropdownRef}
                onMouseEnter={handleAboutMouseEnter}
                onMouseLeave={handleAboutMouseLeave}
              >
                <Link
                  to="#"
                  className="nav-link inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-apn-blue hover:text-apn-blue"
                >
                  About Us
                  <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Link>

                {/* About Dropdown */}
                <div
                  className={`absolute z-10 top-16 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 transition-opacity duration-150 ${isAboutDropdownOpen ? 'opacity-100' : 'opacity-0 invisible'}`}
                >
                  <div className="py-1">
                    <Link to="/about/our-story" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Our Story
                    </Link>
                    <Link to="/about/membership" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Membership Information
                    </Link>
                    <Link to="/about/sponsorship" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Sponsorship
                    </Link>
                    <Link to="/about/additional-services" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Additional Services
                    </Link>
                    <Link to="/about/contact" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      Contact Us
                    </Link>
                  </div>
                </div>
              </div>

              <div className="flex items-center h-16">
                <Link to="/events" className="nav-link inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-apn-blue hover:text-apn-blue">
                  Events
                </Link>
              </div>

              <div className="flex items-center h-16">
                <Link to="/news" className="nav-link inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-apn-blue hover:text-apn-blue">
                  News
                </Link>
              </div>



              {isLoggedIn ? (
                <>
                  <div className="flex items-center h-16">
                    <Link to="/directory" className="nav-link inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-apn-blue hover:text-apn-blue">
                      Member Directory
                    </Link>
                  </div>
                  <div className="flex items-center h-16">
                    <Link to="/groups" className="nav-link inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-apn-blue hover:text-apn-blue">
                      Groups
                    </Link>
                  </div>
                  <div className="flex items-center h-16">
                    <Link to="/jobs" className="nav-link inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-apn-blue hover:text-apn-blue">
                      Job Board
                    </Link>
                  </div>
                  <div className="flex items-center h-16">
                    <Link to="/projects" className="nav-link inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-apn-blue hover:text-apn-blue">
                      Project Board
                    </Link>
                  </div>


                </>
              ) : null}
            </nav>
          </div>

          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            {isLoggedIn ? (
              <div className="flex items-center space-x-4">
                <NotificationBell />
                {isAdmin && (
                  <Link to="/admin" className="nav-link text-red-600 font-medium">
                    Admin Dashboard
                  </Link>
                )}
                <Link to="/profile" className="nav-link">
                  Profile
                </Link>
                <button
                  onClick={onLogout}
                  className="btn-secondary"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="nav-link">
                  Login
                </Link>
                <Link to="/register" className="btn-primary">
                  Register
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-apn-blue hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-apn-blue"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            <div>
              <button
                onClick={toggleAboutDropdown}
                className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium"
              >
                About Us
              </button>
              {isAboutDropdownOpen && (
                <div className="pl-6">
                  <Link to="/about/our-story" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
                    Our Story
                  </Link>
                  <Link to="/about/membership" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
                    Membership Information
                  </Link>
                  <Link to="/about/sponsorship" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
                    Sponsorship
                  </Link>
                  <Link to="/about/additional-services" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
                    Additional Services
                  </Link>
                  <Link to="/about/contact" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800">
                    Contact Us
                  </Link>
                </div>
              )}
            </div>

            <Link to="/events" className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium">
              Events
            </Link>

            <Link to="/news" className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium">
              News
            </Link>



            {isLoggedIn ? (
              <>
                <Link to="/directory" className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium">
                  Member Directory
                </Link>
                <Link to="/groups" className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium">
                  Groups
                </Link>
                <Link to="/jobs" className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium">
                  Job Board
                </Link>
                <Link to="/projects" className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium">
                  Project Board
                </Link>



                <Link to="/profile" className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium">
                  Profile
                </Link>
                <button
                  onClick={onLogout}
                  className="w-full text-left nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium">
                  Login
                </Link>
                <Link to="/register" className="nav-link block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium">
                  Register
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
});  // Close the forwardRef

export default Header;
